import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui";

export interface TableColumn<T> {
  key: string;
  header: string;
  /** When set, used in the desktop header row instead of plain `header` text. */
  headerRender?: ReactNode;
  render: (row: T) => ReactNode;
}

interface TableLayoutProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  /** Custom mobile card per row; when omitted, first columns are shown as a definition list. */
  mobileCardRender?: (row: T, rowIndex: number) => ReactNode;
}

function defaultMobileCard<T>(row: T, columns: TableColumn<T>[]) {
  const preview = columns.slice(0, 5);
  return (
    <dl className="space-y-1.5 text-sm">
      {preview.map((col) => (
        <div key={col.key} className="flex justify-between gap-3 min-w-0">
          <dt className="text-muted-foreground shrink-0">{col.header}</dt>
          <dd className="min-w-0 text-right font-medium">{col.render(row)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function TableLayout<T>({ columns, rows, mobileCardRender }: TableLayoutProps<T>) {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key}>{col.headerRender ?? col.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.render(row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="md:hidden space-y-3">
        {rows.map((row, rowIdx) => (
          <Card key={rowIdx} className="border-border shadow-sm">
            <CardContent className="p-4 pt-4">
              {mobileCardRender ? mobileCardRender(row, rowIdx) : defaultMobileCard(row, columns)}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

/** Semantic alias — same component as `TableLayout` with responsive mobile cards. */
export const ResponsiveTable = TableLayout;
